import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Card } from '../components/Card.tsx';
import { ReportsHeader, usePeriod } from '../components/ReportsHeader.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Technician {
  agentId: string;
  agentName: string;
  agentEmail: string;
  avgCsatScore: number | null;
  csatResponseCount: number;
  firstResponseAvgMinutes: number | null;
  firstResponseCount: number;
  subsequentResponseAvgMinutes: number | null;
  subsequentResponseCount: number;
  articlesAuthored: number;
  billableMinutes: number;
  nonBillableMinutes: number;
  projectsOnTime: number;
  projectsLate: number;
}

interface TechniciansResponse {
  period: { granularity: 'month' | 'quarter'; start: string; end: string; label: string };
  technicians: Technician[];
}

interface WorkEntry {
  id: string;
  startedAt: string;
  ticketId: string | null;
  ticketDisplayNumber: number | null;
  ticketSubject: string | null;
  organizationName: string | null;
  notes: string | null;
  billingBucket: string;
  isBillable: boolean;
  durationMinutes: number;
}

interface DrilldownState {
  loading: boolean;
  error: string | null;
  entries: WorkEntry[] | null;
}

type SortKey =
  | 'name'
  | 'csat'
  | 'firstResponse'
  | 'subResponse'
  | 'articles'
  | 'billable'
  | 'nonBillable'
  | 'projectsOnTime'
  | 'projectsLate';

function formatMinutes(min: number | null): string {
  if (min === null) return '—';
  if (min < 60) return `${min.toFixed(0)}m`;
  if (min < 60 * 24) return `${(min / 60).toFixed(1)}h`;
  return `${(min / (60 * 24)).toFixed(1)}d`;
}

function formatHours(min: number): string {
  return `${(min / 60).toFixed(1)}h`;
}

function formatEntryDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function bucketLabel(bucket: string): string {
  return bucket.replace(/_/g, ' ');
}

export function ReportsTechnicianPerformancePage(): React.ReactElement {
  useDocumentTitle('Technician performance');
  const { agent } = useAuth();
  const { period } = usePeriod();
  const [data, setData] = useState<TechniciansResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('billable');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<Record<string, DrilldownState | undefined>>({});

  // Only admins / time-admins may drill into another agent's work detail;
  // everyone can drill into their own. Mirrors the backend access gate.
  const isTimeAdmin = agent?.role === 'admin' || agent?.role === 'time_admin';

  const periodQuery = useMemo(
    () =>
      new URLSearchParams({
        granularity: period.granularity,
        periodStart: period.periodStart,
      }).toString(),
    [period.granularity, period.periodStart],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setExpanded({});
    apiFetch<TechniciansResponse>(`/api/reports/technicians?${periodQuery}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
  }, [periodQuery]);

  function toggleExpand(agentId: string): void {
    if (expanded[agentId]) {
      setExpanded((prev) => ({ ...prev, [agentId]: undefined }));
      return;
    }
    setExpanded((prev) => ({ ...prev, [agentId]: { loading: true, error: null, entries: null } }));
    apiFetch<{ entries: WorkEntry[] }>(`/api/reports/technicians/${agentId}?${periodQuery}`)
      .then((r) => {
        setExpanded((prev) => ({ ...prev, [agentId]: { loading: false, error: null, entries: r.entries } }));
      })
      .catch((err) => {
        setExpanded((prev) => ({
          ...prev,
          [agentId]: {
            loading: false,
            error: err instanceof ApiError ? err.message : 'Failed to load work detail.',
            entries: null,
          },
        }));
      });
  }

  const sortedTechs = useMemo(() => {
    if (!data) return [];
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: Technician, b: Technician): number => {
      switch (sortKey) {
        case 'name':
          return dir * a.agentName.localeCompare(b.agentName);
        case 'csat':
          return dir * compareNullableNumber(a.avgCsatScore, b.avgCsatScore);
        case 'firstResponse':
          return dir * compareNullableNumber(a.firstResponseAvgMinutes, b.firstResponseAvgMinutes, true);
        case 'subResponse':
          return dir * compareNullableNumber(a.subsequentResponseAvgMinutes, b.subsequentResponseAvgMinutes, true);
        case 'articles':
          return dir * (a.articlesAuthored - b.articlesAuthored);
        case 'billable':
          return dir * (a.billableMinutes - b.billableMinutes);
        case 'nonBillable':
          return dir * (a.nonBillableMinutes - b.nonBillableMinutes);
        case 'projectsOnTime':
          return dir * (a.projectsOnTime - b.projectsOnTime);
        case 'projectsLate':
          return dir * (a.projectsLate - b.projectsLate);
      }
    };
    return [...data.technicians].sort(cmp);
  }, [data, sortKey, sortDir]);

  if (!agent) return <p>Loading…</p>;

  const headerCell = (key: SortKey, label: string, align: 'left' | 'right' = 'right'): React.ReactElement => (
    <th
      className={
        'cursor-pointer select-none px-3 py-2 ' +
        (align === 'right' ? 'text-right' : '') +
        ' hover:text-[var(--color-t3-blue)]'
      }
      onClick={() => {
        if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else {
          setSortKey(key);
          setSortDir(key === 'name' ? 'asc' : 'desc');
        }
      }}
    >
      {label}
      {sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="space-y-6">
      <ReportsHeader
        title="Reports"
        description="Per-technician performance: CSAT, response times, KB articles authored, billable vs non-billable hours, and project delivery. Click a technician to see exactly what they worked on; click any column header to sort."
      />

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {!data ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : sortedTechs.length === 0 ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">No active technicians on record.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden !p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="w-8 px-3 py-2" aria-label="Expand" />
                  {headerCell('name', 'Technician', 'left')}
                  {headerCell('csat', 'Avg CSAT')}
                  {headerCell('firstResponse', 'First reply')}
                  {headerCell('subResponse', 'Subseq reply')}
                  {headerCell('articles', 'KB articles')}
                  {headerCell('billable', 'Billable')}
                  {headerCell('nonBillable', 'Non-billable')}
                  {headerCell('projectsOnTime', 'Proj. on-time')}
                  {headerCell('projectsLate', 'Proj. late')}
                  <th className="px-3 py-2 text-right">CSV</th>
                </tr>
              </thead>
              <tbody>
                {sortedTechs.map((t) => {
                  const canDrill = isTimeAdmin || t.agentId === agent.id;
                  const drill = expanded[t.agentId];
                  const isOpen = drill !== undefined;
                  return (
                    <Fragment key={t.agentId}>
                      <tr
                        className={
                          'border-t border-[var(--color-border)] ' +
                          (canDrill ? 'cursor-pointer hover:bg-[var(--color-surface)]' : '')
                        }
                        onClick={canDrill ? () => toggleExpand(t.agentId) : undefined}
                      >
                        <td className="px-3 py-2 text-[var(--color-muted)]">
                          {canDrill &&
                            (isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            ))}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{t.agentName}</div>
                          <div className="text-xs text-[var(--color-muted)]">{t.agentEmail}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {t.avgCsatScore !== null ? (
                            <span>
                              {t.avgCsatScore.toFixed(2)} ★{' '}
                              <span className="text-xs text-[var(--color-muted)]">({t.csatResponseCount})</span>
                            </span>
                          ) : (
                            <span className="text-[var(--color-muted)]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {formatMinutes(t.firstResponseAvgMinutes)}
                          {t.firstResponseCount > 0 && (
                            <span className="ml-1 text-xs text-[var(--color-muted)]">({t.firstResponseCount})</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {formatMinutes(t.subsequentResponseAvgMinutes)}
                          {t.subsequentResponseCount > 0 && (
                            <span className="ml-1 text-xs text-[var(--color-muted)]">({t.subsequentResponseCount})</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{t.articlesAuthored}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatHours(t.billableMinutes)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[var(--color-muted)]">
                          {formatHours(t.nonBillableMinutes)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">
                          {t.projectsOnTime}
                        </td>
                        <td
                          className={
                            'px-3 py-2 text-right font-mono ' +
                            (t.projectsLate > 0 ? 'text-[var(--color-danger)]' : 'text-[var(--color-muted)]')
                          }
                        >
                          {t.projectsLate}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {canDrill && (
                            <a
                              href={`/api/reports/technicians/export.csv?${periodQuery}&agentId=${t.agentId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center text-[var(--color-t3-blue)] hover:underline"
                              aria-label={`Download CSV for ${t.agentName}`}
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-[var(--color-border)]">
                          <td colSpan={11} className="bg-[var(--color-surface)] px-3 py-3">
                            {drill.loading ? (
                              <p className="text-sm text-[var(--color-muted)]">Loading work detail…</p>
                            ) : drill.error ? (
                              <p className="text-sm text-[var(--color-danger)]">{drill.error}</p>
                            ) : drill.entries && drill.entries.length > 0 ? (
                              <table className="w-full text-sm">
                                <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                                  <tr>
                                    <th className="px-3 py-1.5">When</th>
                                    <th className="px-3 py-1.5">Ticket</th>
                                    <th className="px-3 py-1.5">Client</th>
                                    <th className="px-3 py-1.5">What they worked on</th>
                                    <th className="px-3 py-1.5">Bucket</th>
                                    <th className="px-3 py-1.5 text-right">Hours</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {drill.entries.map((e) => (
                                    <tr key={e.id} className="border-t border-[var(--color-border)]">
                                      <td className="px-3 py-1.5 whitespace-nowrap text-[var(--color-muted)]">
                                        {formatEntryDate(e.startedAt)}
                                      </td>
                                      <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                                        {e.ticketId && e.ticketDisplayNumber !== null ? (
                                          <Link
                                            to={`/tickets/${e.ticketId}`}
                                            className="text-[var(--color-t3-blue)] hover:underline"
                                            onClick={(ev) => ev.stopPropagation()}
                                          >
                                            T-{e.ticketDisplayNumber}
                                          </Link>
                                        ) : (
                                          <span className="text-xs italic text-[var(--color-muted)]">internal</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5">{e.organizationName ?? '—'}</td>
                                      <td className="px-3 py-1.5">
                                        <div>{e.notes ?? '—'}</div>
                                        {e.ticketSubject && (
                                          <div className="text-xs text-[var(--color-muted)]">{e.ticketSubject}</div>
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5 text-xs uppercase tracking-wide text-[var(--color-muted)]">
                                        {bucketLabel(e.billingBucket)}
                                      </td>
                                      <td
                                        className={
                                          'px-3 py-1.5 text-right font-mono ' +
                                          (e.isBillable ? '' : 'text-[var(--color-muted)]')
                                        }
                                      >
                                        {(e.durationMinutes / 60).toFixed(2)}h
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-sm text-[var(--color-muted)]">
                                No time logged in this period.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-muted)]">
            CSAT counts surveys for tickets the tech was assigned to. Response times are the
            tech's own replies. Billable buckets: block_hours, project, quote. Project
            delivery credits the kickoff (earliest type=project) ticket's assignee.
          </div>
        </Card>
      )}
    </div>
  );
}

function compareNullableNumber(a: number | null, b: number | null, lowerIsBetter = false): number {
  // Consistent null handling: nulls always go last. lowerIsBetter is a hint
  // for response-time columns where null means "no samples", which we want
  // to sit at the bottom regardless of sort direction's intent.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return lowerIsBetter ? a - b : a - b;
}
